import { ReactElement, useState } from "react";
import { Stack, Typography } from "@altimateai/lego";
import { DismissibleAlertBanner } from "@/components/banners/DismissibleAlertBanner";
import { DataMatesStats } from "@/pages/teammate/dataMates/DataMatesStats";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@altimateai/lego";
import { SettingsIcon, VSCodeIcon, CursorIcon, ArrowDownIcon } from "@/assets/icons";
import { DateRange } from "@/components/dateRange";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import dayjs from "dayjs";
import { Link, useNavigate } from "react-router-dom";
import { useAppState } from "@/modules/app/useAppContext";
import { getTenant, getBackendUrl } from "@/helpers/tenant";
import { DataMatesList } from "@/pages/teammate/dataMates/DataMatesList";
import { useTeammatesWithStats } from "@/pages/mcp/useTeammates";
import { OnboardDatamate } from "./OnboardDatamate";
import { IconPlus } from "@tabler/icons-react";
import { DataMatesVisibilityFilter, VisibilityFilter } from "./DataMatesFilters";
import { DataMatesSearch } from "./DataMatesSearch";
import { useBreadcrumb } from "@/components/breadcrumbs";

export function DataMatesLandingView(): ReactElement {
  useBreadcrumb("Datamates");
  const navigate = useNavigate();
  const { loggedinUser } = useAppState();
  const [searchQuery, setSearchQuery] = useState("");
  const [visibility, setVisibility] = useState<VisibilityFilter>("all");
  const { startDate, endDate, onUpdate } = useDateFromSearchParams(
    dayjs().subtract(28, "day").toDate(),
    dayjs().toDate()
  );
  const {
    data: teammates,
    usage,
    isLoading,
  } = useTeammatesWithStats({
    start_date: startDate.toISOString(),
    end_date: endDate.toISOString(),
  });

  const tenant = getTenant();
  const apiUrl = getBackendUrl() || "";
  const apiKey = loggedinUser?.auth_token || "";

  const handleVSCodeSetup = () => {
    const deepLink = `vscode://altimateai.vscode-altimate-mcp-server/setup?tenant=${encodeURIComponent(tenant)}&apiKey=${encodeURIComponent(apiKey)}&apiUrl=${encodeURIComponent(apiUrl)}`;
    window.open(deepLink);
  };

  const handleCursorSetup = () => {
    const deepLink = `cursor://altimateai.vscode-altimate-mcp-server/setup?tenant=${encodeURIComponent(tenant)}&apiKey=${encodeURIComponent(apiKey)}&apiUrl=${encodeURIComponent(apiUrl)}`;
    window.open(deepLink);
  };
  return (
    <Stack direction="column" gap="4xl">
      <DismissibleAlertBanner
        bannerId="datamates-landing"
        title="Expand your team with Datamates - your AI Teammates!"
        content={
          <span>
            Do data work faster, better and smarter.{" "}
            <a
              href="https://datamates-docs.myaltimate.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="al-underline al-text-blue-400 hover:al-text-blue-300 al-font-medium"
            >
              Read More
            </a>
          </span>
        }
      />
      <Stack gap="lg">
        <Stack direction="column" gap="sm" justify="center">
          <Typography variant="h3">DataMates</Typography>
          <Typography variant="body">Your AI teammates for data tasks</Typography>
        </Stack>
        <Stack justify="end" align="center" className="al-flex-1">
          <DateRange
            startDate={startDate}
            endDate={endDate}
            onDateRangeSelect={(startDate, endDate) => {
              onUpdate(startDate, endDate);
            }}
          />
        </Stack>
      </Stack>
      <DataMatesStats
        isLoading={isLoading}
        usage={usage}
        start_date={startDate}
        end_date={endDate}
      ></DataMatesStats>
      <Stack align="center">
        <Typography variant="h4">My Datamates</Typography>
        <Stack justify="end" align="center" gap="sm" className="al-flex-1">
          <div className="al-w-[200px] al-shrink-0">
            <DataMatesSearch searchQuery={searchQuery} onSearchChange={setSearchQuery} />
          </div>
          <DataMatesVisibilityFilter visibility={visibility} onVisibilityChange={setVisibility} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <SettingsIcon />
                <span className="al-text-sm">Configure Extension</span>
                <ArrowDownIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="al-w-64">
              <div className="al-px-3 al-py-2 al-text-sm al-text-gray-600">
                Configure your <strong>Datamates extension</strong> in the following IDEs
              </div>
              <DropdownMenuItem onClick={handleVSCodeSetup} className="al-cursor-pointer">
                <VSCodeIcon />
                <span>VS Code</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleCursorSetup} className="al-cursor-pointer">
                <CursorIcon />
                <span>Cursor</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" asChild>
            <Link to="/datamates/memories">View Memories</Link>
          </Button>
          <Button onClick={() => navigate("/datamates/create")}>
            <IconPlus />
            <span className="al-text-sm">Add Datamate</span>
          </Button>
        </Stack>
      </Stack>
      <DataMatesList
        teammatesFromServer={teammates}
        isLoading={isLoading}
        usage={usage}
        searchQuery={searchQuery}
        visibility={visibility}
      />
      <OnboardDatamate />
    </Stack>
  );
}
